import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/ui/hover-card';
import { Button } from '../../../../components/ui/button';
import { formatCurrency } from '../../../../../lib/utils';
import { Badge } from '../../../../components/ui/badge';
import { useAnalyticsContext } from '../analytics-context';
import React from 'react';

export function ChartTransactions() {
  const { transactions, selectedFilter, payload } = useAnalyticsContext();
  const response = transactions.data;

  const chartData =
    response?.chartData?.labels.map((label, index) => {
      const dataPoint: any = { label };
      response.chartData.datasets.forEach(dataset => {
        dataPoint[dataset.label] = dataset.data[index];
      });
      return dataPoint;
    }) || [];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: any) => (
            <div key={entry.name} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm">
                {entry.name}: {formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="flex flex-col border-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold mb-0">
          Transactions Overview
        </CardTitle>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            {response?.chartData?.datasets.slice(0, 1).map(dataset => (
              <div
                key={dataset.label}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <span
                  className="h-2 w-2 rounded-full inline-block"
                  style={{ backgroundColor: dataset.borderColor }}
                />
                <span className="text-sm text-muted-foreground">
                  {dataset.label}:
                </span>
                <span className="font-medium text-foreground text-sm">
                  {formatCurrency(
                    response?.summary?.paymentMethodTotals?.[dataset.label]
                      ?.amount || 0,
                  )}
                </span>
                <span className="text-sm text-muted-foreground">
                  (
                  {response?.summary?.paymentMethodTotals?.[dataset.label]
                    ?.transactions || 0}{' '}
                  txn)
                </span>
              </div>
            ))}
            {response?.chartData?.datasets.length > 1 && (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                  >
                    +{response.chartData.datasets.length - 1} more
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent className="w-auto p-3">
                  <div className="flex flex-col gap-2">
                    {response.chartData.datasets.slice(1).map(dataset => (
                      <div
                        key={dataset.label}
                        className="flex items-center gap-2 whitespace-nowrap"
                      >
                        <span
                          className="h-2 w-2 rounded-full inline-block"
                          style={{ backgroundColor: dataset.borderColor }}
                        />
                        <span className="text-sm text-muted-foreground">
                          {dataset.label}:
                        </span>
                        <span className="font-medium text-foreground text-sm">
                          {formatCurrency(
                            response?.summary?.paymentMethodTotals?.[
                              dataset.label
                            ]?.amount || 0,
                          )}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          (
                          {response?.summary?.paymentMethodTotals?.[
                            dataset.label
                          ]?.transactions || 0}{' '}
                          txn)
                        </span>
                      </div>
                    ))}
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
          </div>
          <Badge variant="outline" className="capitalize">
            {selectedFilter === 'custom'
              ? 'Custom range'
              : selectedFilter.toLowerCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        {transactions.isLoading ? (
          <div className="h-[400px] flex items-center justify-center">
            <div className="h-72 w-full animate-pulse rounded-xl bg-muted/60" />
          </div>
        ) : selectedFilter === 'custom' && payload === null ? (
          <div className="h-[400px] flex items-center justify-center text-muted-foreground">
            Select a start and end date to view custom data.
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[400px] flex items-center justify-center text-muted-foreground">
            No data available for the selected period
          </div>
        ) : (
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 50, right: 40, left: 30, bottom: 40 }}
              >
                <CartesianGrid
                  strokeDasharray="4 4"
                  className="stroke-muted/50"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={true}
                  tickMargin={16}
                  className="text-muted-foreground text-xs"
                />
                <YAxis
                  tickMargin={16}
                  axisLine={true}
                  tickLine={false}
                  allowDecimals={true}
                  className="text-muted-foreground text-xs"
                  tickFormatter={value => formatCurrency(value)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  height={60}
                  content={({ payload }) => (
                    <div className="flex justify-end gap-4 mb-4">
                      {payload?.map((entry: any) => (
                        <div
                          key={entry.value}
                          className="flex items-center gap-2"
                        >
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-sm text-muted-foreground">
                            {entry.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                />
                {response?.chartData?.datasets.map(dataset => (
                  <Line
                    key={dataset.label}
                    type="monotone"
                    dataKey={dataset.label}
                    stroke={dataset.borderColor}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
